/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Col, Select, Space, Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import moment from "moment";
import { CalendarOutlined, DownloadOutlined } from "@ant-design/icons";
import { FileOutlined, FileTextOutlined } from "@ant-design/icons/lib/icons";
import { Filters, FiltersCard } from "../../../components/Filters";
import { Label } from "../../../components/ui";

import api from "../../../services/api";

import { useToast } from "../../../hooks/toast";
// eslint-disable-next-line import/extensions
import Table from "../../../components/Table";
import UserAvatar from "../../../components/UserAvatar";
import { ICustomer } from "../../Customers";
import { IUser } from "../../Users";
import { useAuth } from "../../../hooks/auth";
import ProjectTypes from "../../../enums/ProjectTypes";
import { IRequirement } from "../../Project";
import AllocationStatus from "../../../enums/AllocationStatus";
import TimeSheetRecordingModal from "../TimeSheetRecordingModal";
import TimeSheetAllocationModal from "../../Allocations/AllAllocationsActivities/TimeSheetAllocationModal";
import userRoles from "../../../enums/UserRoles";

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const AllocationsActivities: React.FC = () => {
  const [selectedActivity, setSelectedActivity] = useState<IRequirement>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [allocationMonths, setAllocationMonths] = useState<any[]>([]);
  const [showAllocationFilters, setShowAllocationFilters] = useState(false);
  const [modalVisibleSheet, setModalVisibleSheet] = useState<boolean>(false);
  const [type, setType] = useState<"number_nf" | "number_fiche_service">(
    "number_nf",
  );

  const { addToast } = useToast();
  const { t } = useTranslation(["activities"]);
  const { user } = useAuth();

  // Filters
  const [allocationStatus, setAllocationStatus] = useState();

  const allocationSearchParams = useMemo(() => {
    return {
      status: allocationStatus,
      allocation: true,
    };
  }, [allocationStatus]);

  useEffect(() => {
    setLoadingAllocations(true);
    api
      .get<IRequirement[]>("/activities", { params: allocationSearchParams })
      .then(async response => {
        setAllocationMonths(response.data);
        const promises: any[] = [];
        response.data.forEach(activity => {
          const status = new Date() < new Date(activity.year, activity.month);
          if (!status && !activity?.report_fiche_service) {
            const message = `Informe a Folha de Serviço para ${activity?.consultant?.name} no projeto "${activity?.project?.short_description}"`;
            promises.push(
              api.post(
                `/activities/${activity?.project?.id}/notification/${activity?.consultant_id}`,
                { status: true, message, activity_id: activity.id },
              ),
            );
          }
        });
        await Promise.all(promises);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os seus meses de alocação, tente novamente.",
        });
      })
      .finally(() => setLoadingAllocations(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationSearchParams, modalVisibleSheet]);

  const allocationStatusOptions = useMemo(() => {
    return (Object.keys(
      AllocationStatus,
    ) as (keyof typeof AllocationStatus)[]).map((key, _) => {
      return {
        label: t(`status.options.${AllocationStatus[key]}.label`),
        value: AllocationStatus[key],
      };
    });
  }, [t]);

  const userValidate = useMemo(() => {
    return (
      user.role === userRoles.Administrative || user.role === userRoles.Manager
    );
  }, [user]);

  const handleOpenSheetModal = (
    type: "number_nf" | "number_fiche_service",
    activity?: IRequirement,
  ): void => {
    setType(type);
    setSelectedActivity(activity);
    setModalVisibleSheet(true);
  };

  const handleOpenModal = useCallback((activity: IRequirement) => {
    setSelectedActivity(activity);
    setModalVisible(true);
  }, []);

  const handleExportReport = useCallback((activity: IRequirement) => {
    api
      .get(`/activities/${activity.id}/schedule-file`, {
        responseType: "arraybuffer",
      })
      .then(response => {
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${activity.consultant?.name}_${activity.month + 1}_${
          activity.year
        }_timesheet.xlsx`;
        link.click();
      });
  }, []);

  const allocationMonthsColumns: ColumnsType<IRequirement> = [
    {
      title: t("project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project?.id}`}>
              {project?.short_description}
            </Link>
          </>
        );
      },
    },
    {
      title: t("allocation_month"),
      dataIndex: "month",
      render: (_, record: IRequirement) => {
        const date = new Date(record.year, record.month);
        return moment(date).format("MMMM [de] YYYY");
      },
    },
    {
      title: t("hours"),
      dataIndex: "hours",
      render: (hours: number) => hours && `${hours} h`,
    },
    {
      title: t("consultant"),
      dataIndex: "consultant",
      render: (consultant: IUser) => {
        if (consultant) {
          return (
            <UserAvatar
              name={consultant.name}
              url={consultant.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Tooltip title="Apontar horas">
            <Button
              onClick={() => handleOpenModal(record)}
              type="link"
              disabled={new Date() > new Date(record.year, record.month + 1, 4)}
              icon={<CalendarOutlined />}
            />
          </Tooltip>
          <Tooltip title="Exportar planilha">
            <Button
              onClick={() => handleExportReport(record)}
              type="link"
              disabled={new Date() < new Date(record.year, record.month)}
              icon={<DownloadOutlined />}
            />
          </Tooltip>
          {userValidate && (
            <Tooltip title="Informar Folha de Serviço">
              <Button
                onClick={
                  () => handleOpenSheetModal("number_fiche_service", record)
                  // ts-ignore
                }
                type="link"
                disabled={new Date() < new Date(record.year, record.month)}
                icon={<FileOutlined />}
              />
            </Tooltip>
          )}
          {record.number_fiche_service && userValidate && (
            <Tooltip title="Informar Número NFS">
              <Button
                onClick={() => handleOpenSheetModal("number_nf", record)}
                type="link"
                icon={<FileTextOutlined />}
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const handleShowAllocationFilters = useCallback(
    value => {
      setShowAllocationFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showAllocationFilters) {
        setAllocationStatus(undefined);
      }
    },
    [showAllocationFilters],
  );

  const allocationsFilters = useMemo(
    () =>
      showAllocationFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("status.label")}</Label>
            </div>
            <Select
              placeholder={t("status.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={allocationStatusOptions}
              onChange={value => setAllocationStatus(value)}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [allocationStatusOptions, showAllocationFilters, t],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedActivity(undefined);
    setModalVisibleSheet(false);
    setModalVisible(false);
  }, []);

  return (
    <>
      <TimeSheetRecordingModal
        activity={selectedActivity}
        visible={modalVisible}
        handleClose={handleCloseModal}
      />
      <TimeSheetAllocationModal
        activity={selectedActivity}
        visible={modalVisibleSheet}
        handleClose={handleCloseModal}
        type={type}
      />
      {allocationMonths.length > 0 && (
        <Space
          direction="vertical"
          style={{ display: "flex", marginBottom: 16 }}
        >
          <Table
            data={allocationMonths}
            columns={allocationMonthsColumns}
            title={
              <>
                Meses de alocação
                <Filters
                  showFilters={showAllocationFilters}
                  setShowFilters={handleShowAllocationFilters}
                />
              </>
            }
            footer={() => <></>}
            loading={loadingAllocations}
            pagination={false}
            extraFilters={allocationsFilters}
          />
        </Space>
      )}
    </>
  );
};

export default AllocationsActivities;
